import React from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import DocumentAbstractHit from '@Components/Results/documents/DocumentAbstractHit';
import DocumentAbstract from '@Components/Results/documents/DocumentAbstract';
import DocumentClaims from '@Components/Results/documents/DocumentClaims';
import DocumentBibliographicInfo from '@Components/Results/documents/DocumentBibliographicInfo';
import DocumentIndexTermsHit from '@Components/Results/documents/DocumentIndexTermsHit';
import DocumentIndexTerms from '@Components/Results/documents/DocumentIndexTerms';
import DocumentSubstances from '@Components/Results/documents/DocumentSubstances';
import DocumentReactions from '@Components/Results/documents/DocumentReactions';
import DocumentTargets from '@Components/Results/documents/DocumentTargets';

/**
 * Renders the details area of a document result.
 *
 * When no category is open, the matched-text hit cards (Abstract Hit / Index Terms
 * Hit) are shown by default — each self-hides if it has no highlighted terms.
 * Clicking a hit card, or any navigation item, opens that section's full content
 * (`onToggle`) and hides the hit cards; closing it (`onClose`) returns to the
 * default hit-card view.
 */
function DocumentCollapsibleContent({ metadata, document: citation, zoom, activeId, onToggle, onClose, getTrackingAttrs }) {
  const documentIndex = citation.index;
  const documentRelativeIndex = citation.relativeIndex;

  // Default view (nothing open): matched-text hit cards. Each hit component
  // self-hides when its text has no highlighted terms.
  if (!activeId) {
    if (!citation.abstractText && !citation.keywords) return null;

    return (
      <div className="document-hit-cards">
        {!!citation.abstractText && (
          <DocumentAbstractHit
            abstractText={citation.abstractText}
            onOpen={() => onToggle('abstractExpanded')}
            trackingAttrs={getTrackingAttrs({
              'data-tracking-type': 'contentview',
              'data-tracking-action': 'abstract hit',
            })}
          />
        )}
        {!!citation.keywords && (
          <DocumentIndexTermsHit
            keywords={citation.keywords}
            onOpen={() => onToggle('indextermsExpanded')}
            trackingAttrs={getTrackingAttrs({
              'data-tracking-type': 'contentview',
              'data-tracking-action': 'index terms hit',
            })}
          />
        )}
      </div>
    );
  }

  switch (activeId) {
    case 'abstractExpanded':
      return <DocumentAbstract abstractText={citation.abstractText} closeClick={onClose} />;

    case 'claimsExpanded':
      return <DocumentClaims claimsText={citation.patentClaims} closeClick={onClose} />;

    case 'indextermsExpanded':
      return <DocumentIndexTerms keywords={citation.keywords} closeClick={onClose} />;

    case 'substancesExpanded':
      return (
        <DocumentSubstances
          key="substancesExpanded"
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          substancesCount={citation.substancesCount}
          isHit={false}
          zoom={zoom}
          closeClick={onClose}
        />
      );

    case 'hitSubstancesExpanded':
      return (
        <DocumentSubstances
          key="hitSubstancesExpanded"
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          substancesCount={citation.substancesHitCount}
          isHit
          zoom={zoom}
          closeClick={onClose}
        />
      );

    case 'reactionsExpanded':
      return (
        <DocumentReactions
          key="reactionsExpanded"
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          reactionsCount={citation.reactionsCount}
          isHit={false}
          zoom={zoom}
          closeClick={onClose}
        />
      );

    case 'hitReactionsExpanded':
      return (
        <DocumentReactions
          key="hitReactionsExpanded"
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          reactionsCount={citation.reactionsHitCount}
          isHit
          zoom={zoom}
          closeClick={onClose}
        />
      );

    case 'targetsExpanded':
      return (
        <DocumentTargets
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          targetsCount={citation.targetsCount}
          closeClick={onClose}
        />
      );

    case 'bibliographicInfoExpanded':
      return <DocumentBibliographicInfo document={citation} closeClick={onClose} />;

    default:
      return null;
  }
}

DocumentCollapsibleContent.propTypes = {
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  activeId: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // for analytics
  getTrackingAttrs: PropTypes.func.isRequired,
};

export default DocumentCollapsibleContent;
